import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Check, X } from "lucide-react";
import { cn } from "lib/utils";

// --- Safe Message Parser (Prevents React 19 crashes on object errors) ---
export const formatToastMessage = (msg) => {
  if (!msg) return "";
  if (typeof msg === "string") return msg;
  if (Array.isArray(msg)) {
    return msg
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && item.msg) return item.msg;
        if (item && item.message) return item.message;
        try {
          return JSON.stringify(item);
        } catch (e) {
          return String(item);
        }
      })
      .filter(Boolean)
      .join(", ");
  }
  if (typeof msg === "object") {
    if (msg.message && typeof msg.message === "string") return msg.message;
    if (msg.msg && typeof msg.msg === "string") return msg.msg;
    if (msg.detail) return formatToastMessage(msg.detail);
    try {
      return JSON.stringify(msg);
    } catch (e) {
      return String(msg);
    }
  }
  return String(msg);
};

// --- Window-Persistent State (100% resilient across Webpack HMR reloads) ---
const getToastStore = () => {
  if (typeof window === "undefined") {
    return { toasts: [], listeners: new Set(), timers: new Map() };
  }
  if (!window.__AO_TOAST_STORE__) {
    window.__AO_TOAST_STORE__ = {
      toasts: [],
      listeners: new Set(),
      timers: new Map(),
    };
  }
  return window.__AO_TOAST_STORE__;
};

const notify = () => {
  const store = getToastStore();
  const current = [...store.toasts];
  store.listeners.forEach((listener) => {
    try {
      listener(current);
    } catch (e) {
      console.warn("Toast listener error:", e);
    }
  });
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("ao:toast:change", { detail: current }));
  }
};

const clearTimer = (id) => {
  const store = getToastStore();
  if (store.timers.has(id)) {
    clearTimeout(store.timers.get(id));
    store.timers.delete(id);
  }
};

export const removeToast = (id) => {
  const store = getToastStore();
  clearTimer(id);
  store.toasts = store.toasts.filter((t) => t.id !== id);
  notify();
};

const addOrUpdateToast = ({
  type = "success",
  title,
  message,
  id,
  duration = 3500,
}) => {
  const store = getToastStore();
  const toastId = id || `toast_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
  clearTimer(toastId);

  const cleanMessage = formatToastMessage(message);
  const resolvedTitle =
    title || (type === "success" ? "Success" : "Error");

  const newToast = {
    id: toastId,
    type,
    title: resolvedTitle,
    message: cleanMessage,
    duration,
  };

  const existingIndex = store.toasts.findIndex((t) => t.id === toastId);
  if (existingIndex > -1) {
    store.toasts[existingIndex] = newToast;
  } else {
    store.toasts.push(newToast);
  }
  notify();

  if (duration > 0) {
    const timer = setTimeout(() => {
      removeToast(toastId);
    }, duration);
    store.timers.set(toastId, timer);
  }

  return toastId;
};

export const toast = {
  success: (message, options = {}) =>
    addOrUpdateToast({ type: "success", message, ...options }),
  error: (message, options = {}) =>
    addOrUpdateToast({ type: "error", message, ...options }),
  dismiss: (id) => {
    const store = getToastStore();
    if (id) {
      removeToast(id);
    } else {
      store.toasts.forEach((t) => clearTimer(t.id));
      store.toasts = [];
      notify();
    }
  },
};

// Expose globally for instant testing in browser console
if (typeof window !== "undefined") {
  window.toast = toast;
}

// --- Toaster Component ---
export const Toaster = () => {
  const [toasts, setToasts] = useState(() => getToastStore().toasts);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const store = getToastStore();

    const updateHandler = (newToasts) => setToasts(newToasts);
    store.listeners.add(updateHandler);

    // Initial sync
    setToasts([...store.toasts]);

    const domHandler = (e) => {
      if (e.detail) setToasts([...e.detail]);
    };
    window.addEventListener("ao:toast:change", domHandler);

    return () => {
      store.listeners.delete(updateHandler);
      window.removeEventListener("ao:toast:change", domHandler);
    };
  }, []);

  if (!mounted || typeof document === "undefined") return null;

  return createPortal(
    <>
      <style>{`
        @keyframes toast-progress {
          from { width: 100%; }
          to { width: 0%; }
        }
        @keyframes toast-slide-down {
          0% {
            opacity: 0;
            transform: translateY(-14px) scale(0.96);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .ao-toast-enter {
          animation: toast-slide-down 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
      <div
        aria-live="polite"
        className="fixed top-20 right-6 z-[999999] pointer-events-none flex flex-col gap-2.5 max-w-sm w-full sm:w-[360px]"
      >
        {toasts.map((item) => {
          const isSuccess = item.type === "success";

          return (
            <div
              key={item.id}
              className={cn(
                "ao-toast-enter pointer-events-auto relative overflow-hidden flex items-start gap-3 p-3.5 pr-3 rounded-xl border bg-white text-gray-900",
                "border-gray-200/90 shadow-[0_10px_25px_-5px_rgba(0,0,0,0.1),0_8px_10px_-6px_rgba(0,0,0,0.06),0_0_0_1px_rgba(0,0,0,0.02)]",
                "transition-all duration-300 ease-out"
              )}
              role="alert"
            >
              {/* Crisp Solid Status Badge */}
              <div
                className={cn(
                  "w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 shadow-xs",
                  isSuccess
                    ? "bg-emerald-600 text-white shadow-emerald-600/30"
                    : "bg-rose-600 text-white shadow-rose-600/30"
                )}
              >
                {isSuccess ? (
                  <Check size={13} strokeWidth={3} />
                ) : (
                  <X size={13} strokeWidth={3} />
                )}
              </div>

              {/* Title & Message Hierarchy */}
              <div className="flex-1 min-w-0 pr-1">
                <h4 className="text-xs font-semibold text-gray-900 leading-tight">
                  {item.title}
                </h4>
                <p className="text-xs text-gray-600 font-normal mt-0.5 leading-snug break-words">
                  {item.message}
                </p>
              </div>

              {/* Dismiss Button */}
              <button
                type="button"
                onClick={() => removeToast(item.id)}
                className="w-5 h-5 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors shrink-0 -mt-0.5 cursor-pointer"
                aria-label="Close notification"
              >
                <X size={13} strokeWidth={2.2} />
              </button>

              {/* Micro Countdown Progress Bar */}
              {item.duration > 0 && (
                <div
                  className={cn(
                    "absolute bottom-0 left-0 h-[2.5px] rounded-b-xl",
                    isSuccess ? "bg-emerald-500" : "bg-rose-500"
                  )}
                  style={{
                    animation: `toast-progress ${item.duration}ms linear forwards`,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </>,
    document.body
  );
};

export default toast;
